import { twMerge } from '@/lib/classname/twMerge'
import { CloudflarePicture } from '@/ui/media/cloudflare-picture/CloudflarePicture.client'
import { Title } from '@/ui/text/title/Title'
import { type Image, Media } from '@gql-generated/graphql'

const FULL_IMAGE_CARD_DESKTOP_WIDTH = {
  lg: 506,
  md: 245,
  auto: 506,
} as const

const FULL_IMAGE_CARD_HEIGHT = 300
const FULL_IMAGE_CARD_MOBILE_WIDTH = 245

export type FullImageCardSize = keyof typeof FULL_IMAGE_CARD_DESKTOP_WIDTH

export type FullImageCardProps = UiComponentProps<{
  image: Image
  title?: string
  subtitle?: string | null
  size?: FullImageCardSize
  index: number
}> &
  WithPayloadBlockId

export function FullImageCard({
  image,
  title,
  subtitle,
  size = 'md',
  className,
  index,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  'data-payload-block-id': dataPayloadBlockId,
}: FullImageCardProps): React.ReactElement {
  const rootTestId = dataTestId ?? 'full-image-card'

  return (
    <div
      id={id}
      className={twMerge(
        className,
        'relative flex h-[18.75rem] w-[15.3125rem] items-center justify-center overflow-hidden rounded-lg',
        size === 'lg' && 'desktop:w-[31.625rem]',
        size === 'auto' && 'w-auto',
      )}
      aria-label={ariaLabel}
      data-testid={rootTestId}
      data-payload-block-id={dataPayloadBlockId}
    >
      {image && (
        <div
          data-testid={`${rootTestId}-picture/wrapper`}
          className="absolute left-0 top-0 size-full"
        >
          <CloudflarePicture
            src={
              image?.set?.find((set) => set.media === Media.Mobile)?.url ??
              image?.set?.[0]?.url ??
              ''
            }
            alt={image?.alt ?? ''}
            width={{
              mobile: FULL_IMAGE_CARD_MOBILE_WIDTH,
              desktop: FULL_IMAGE_CARD_DESKTOP_WIDTH[size],
            }}
            height={{ mobile: FULL_IMAGE_CARD_HEIGHT, desktop: FULL_IMAGE_CARD_HEIGHT }}
            className="size-full rounded-md"
            loadingStrategy={index === 0 ? 'eager' : 'lazy'}
            fetchPriority={index === 0 ? 'high' : undefined}
            isInternalPicture
          />
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-card" />
      <div className="absolute inset-0 top-[55%] flex flex-col overflow-y-auto px-3 text-white no-scrollbar">
        {title && (
          <Title
            as="h3"
            size="2xl"
            className="mb-1 font-bold group-data-[hovered]:underline"
            data-testid={`${rootTestId}/title`}
          >
            {title}
          </Title>
        )}
        {subtitle && <p data-testid={`${rootTestId}/subtitle`}>{subtitle}</p>}
      </div>
    </div>
  )
}
